/**
 * Parse REACT_APP_* booleans: "true" / "1" → true; "false" / "0" → false; unset → default.
 */
const envFlag = (key: string, defaultValue: boolean): boolean => {
  const raw = process.env[key];
  if (raw === undefined || raw === '') {
    return defaultValue;
  }
  const normalized = String(raw).trim().toLowerCase();
  if (normalized === 'true' || normalized === '1') {
    return true;
  }
  if (normalized === 'false' || normalized === '0') {
    return false;
  }
  return defaultValue;
};

/**
 * When true, ISO 14067 reporting surfaces and related copy are hidden in the web app.
 * Default: visible (false).
 */
export const HIDE_ISO_14067_UI = envFlag('REACT_APP_HIDE_ISO_14067_UI', false);

/**
 * When true, the Documents page omits transaction mapping / product attribution
 * controls and does not display extracted or user-mapped company–product detail.
 * Default: hidden (true) unless REACT_APP_HIDE_DOCUMENTS_SENSITIVE_INPUTS_UI=false.
 */
export const HIDE_DOCUMENTS_SENSITIVE_INPUTS_UI = envFlag(
  'REACT_APP_HIDE_DOCUMENTS_SENSITIVE_INPUTS_UI',
  true
);

export type BillUploadFormat = 'spreadsheet' | 'pdf' | 'image';

export interface BillUploadSettings {
  billOcrEnabled: boolean;
  allowedBillUploadFormats: Record<BillUploadFormat, boolean>;
}

// Set from GET /documents/upload-settings (the admin's choice). Until it loads, the build-time flag applies.
let runtimeBillUploadSettings: BillUploadSettings | null = null;
const billUploadSettingsListeners = new Set<() => void>();

export const setBillUploadSettings = (settings: BillUploadSettings | null): void => {
  runtimeBillUploadSettings = settings;
  billUploadSettingsListeners.forEach((listener) => listener());
};

export const subscribeBillUploadSettings = (listener: () => void): (() => void) => {
  billUploadSettingsListeners.add(listener);
  return () => {
    billUploadSettingsListeners.delete(listener);
  };
};

export const getBillUploadSettingsSnapshot = (): BillUploadSettings | null => runtimeBillUploadSettings;

/**
 * Bill, receipt, and invoice uploads run through document OCR and feed
 * carbon footprint, reporting, analytics, predictions, and recommendations.
 * An admin switches bill reading on or off (Admin dashboard > Platform Features); before the setting has
 * loaded, REACT_APP_BILL_UPLOAD_ENABLED (true or 1) applies.
 */
export const isBillUploadEnabled = (): boolean => (runtimeBillUploadSettings
  ? runtimeBillUploadSettings.billOcrEnabled
  : envFlag('REACT_APP_BILL_UPLOAD_ENABLED', false));

/** Bill formats the admin currently accepts (all of them until the setting has loaded). */
export const getAllowedBillUploadFormats = (): Record<BillUploadFormat, boolean> => (
  runtimeBillUploadSettings?.allowedBillUploadFormats || { spreadsheet: true, pdf: true, image: true }
);

export const BILL_UPLOAD_DISABLED_MESSAGE =
  'Bill upload is turned off by the administrator. Use accounting connectors or enter activity details by hand until it is turned back on.';

const BILL_OCR_DOCUMENT_TYPES = new Set(['bill', 'receipt', 'invoice']);

export const isBillOcrDocumentType = (documentType?: string | null): boolean =>
  BILL_OCR_DOCUMENT_TYPES.has(String(documentType || '').trim().toLowerCase());
